import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Chip, Grid, styled, Checkbox } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  initDriveAuth,
  ICalendarAccount as Account,
  getActiveAccounts,
  IDriveFolder,
  deleteAccount,
  getListOfFoldersInAccount,
  getSkillsSuggestions,
  GoogleDrive,
  createBatchApi,
  importResumeFromDrive,
  ICVBulkUploadDTO,
} from 'wisestep-api';
import {
  Button,
  WsAutoComplete,
  WsButton,
  WsColors,
  WsLocationSearch,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { WsTagsFilter } from '../../../../components/filters/components';
import WsTeamFilter from '../../../../components/filters/components/WsTeamsFilter';
import { getSocialIcon } from '../../../../utils/getSocialIcon';
import { warning, success, failed } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';

import UploadGoogleDriveHeader from './UploadGoogleDriveHeader';

const SignupButton = styled(Button)(() => ({
  color: WsColors.GenericRichBlack,
  fontSize: '18px',
  height: '46px',
  justifyContent: 'flex-start',
  paddingLeft: '22px',
  borderColor: '#EAEAF1',
}));
const LayoutTheme = styled(Box)(() => ({
  minHeight: '800px',
  backgroundColor: 'white',
}));

const UploadFromGoogleDrive = () => {
  const methods = useForm();
  const { setValue, getValues, reset } = methods;
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [folder, setFolders] = useState<IDriveFolder[]>([]);
  const [selectedFolder, setSelectedFolders] = useState<IDriveFolder[]>([]);
  const [folderStack, setFolderStack] = useState<IDriveFolder[]>([]);
  const [importFolderIds, setImportFolderIds] = useState<string[]>([]);
  const [driveDetails, setDriveDetails] = useState<GoogleDrive>();
  const [skillList, setSkillList] = useState<string[]>([]);
  const [selectedFolderCounts, setSelectedFolderCounts] = useState<Record<string, number>>({});

  const googleDriveSignIn = async () => {
    const response = await initDriveAuth();
    window.location.href = response.data.url;
  };

  const onSkillInputKey = async (skillInput: string) => {
    const result = await getSkillsSuggestions(skillInput);
    if (result.data) {
      setSkillList(result.data?.map((item) => item.skill));
    }
  };

  const onSkillSelection = (skillTags: string[]) => {
    setValue('skills', skillTags ?? []);
  };

  const loadChildFolders = async (folder?: IDriveFolder | null, value?: Account | null) => {
    if (value) {
      setSelectedFolders([]);
      setFolders([]);
    }
    if (folder) {
      setSelectedFolders((folders) => [...folders, folder]);
      setFolderStack((stack) => [...stack, folder]);
    }
    const request: GoogleDrive = {
      userEmail: value?.email ?? selectedAccount?.email,
      userId: value?.id ?? selectedAccount?.id,
      folderId: folder ? folder.id : '',
    };
    const folderInAccount = await getListOfFoldersInAccount(request);
    setDriveDetails(folderInAccount.data);
    setFolders(folderInAccount.data.googleDriveFileDTOS);
  };

  const loadMore = async () => {
    const request: GoogleDrive = {
      userEmail: selectedAccount?.email,
      userId: selectedAccount?.id,
      pageToken: driveDetails?.pageToken,
    };
    const folderInAccount = await getListOfFoldersInAccount(request);
    setDriveDetails((oldDrive) => ({ ...oldDrive, pageToken: folderInAccount.data.pageToken }));
    setFolders((oldFolders) => [...oldFolders, folderInAccount.data.googleDriveFileDTOS]);
  };

  const deleteGoogleAccount = (account: Account) => {
    const request: GoogleDrive = {
      userEmail: account.email,
      userId: account.id,
    };
    deleteAccount(request);
  };

  const getCurrentPath = () => '/' + selectedFolder.map((folder) => folder.title).join('/');

  useEffect(() => {
    const getAccounts = async () => {
      const response = await getActiveAccounts();
      setAccounts(response.data);
    };
    getAccounts();
  }, []);

  const selectImportFolders = (folderId: string) => {
    const idx = importFolderIds.indexOf(folderId);
    const selectedFolder = folder.find((folder) => folder.id === folderId);
    if (selectedFolder) {
      if (idx > -1) {
        setImportFolderIds((importIds) => importIds.filter((id) => id !== folderId));
        setSelectedFolderCounts((counts) => {
          const updatedCounts = { ...counts };
          delete updatedCounts[folderId];
          return updatedCounts;
        });
      } else {
        setImportFolderIds((importIds) => [...importIds, folderId]);
        setSelectedFolderCounts((counts) => {
          const updatedCounts = { ...counts };
          updatedCounts[folderId] = selectedFolder.fileCount || 0;
          return updatedCounts;
        });
      }
    }
  };

  const navigateBack = () => {
    if (folderStack.length > 1) {
      const reqFolder = folderStack[folderStack.length - 2];
      setFolderStack((stack) => stack.slice(0, -1));
      getCurrentPath().replace(/\/[^/]*$/, '');
      loadChildFolders(reqFolder, null);
    } else {
      loadChildFolders(null, selectedAccount);
    }
  };

  const startImport = async () => {
    if (isEmpty(getValues('locations'))) {
      setNotification(
        warning('Please select a country for which you are uploading the Candidates')
      );
      return;
    }
    const totalFileCount = Object.values(selectedFolderCounts).reduce(
      (sum, count) => sum + count,
      0
    );
    const processRequest: ICVBulkUploadDTO = {
      totalSelected: totalFileCount,
      importFrom: 'GoogleDrive',
      skills: getValues('skills'),
      tags: getValues('tags'),
      locations: getValues('locations'),
      team: getValues('team'),
    };
    const batchDetails = await createBatchApi(processRequest);
    const isRoot = selectedFolder.length == 0;
    const selectedOptions = getValues();
    const request: GoogleDrive = {
      userEmail: selectedAccount?.email,
      userId: selectedAccount?.id,
      skills: selectedOptions.skills,
      tags: selectedOptions.tags,
      locations: selectedOptions.locations,
      team: selectedOptions.team,
      importFolderIds:
        importFolderIds.length > 0
          ? importFolderIds
          : isRoot
          ? []
          : [selectedFolder[selectedFolder.length - 1].id ?? ''],
      batchId: batchDetails.data.id,
      batchCode: batchDetails.data.batchCode,
    };
    try {
      await importResumeFromDrive(request);
      reset();
      setSelectedAccount(null);
      setSelectedFolders([]);
      setFolders([]);
      setDriveDetails(undefined);
      setNotification(
        success(
          'Import process has been scheduled successfully. It may take a while depending on the number of files.'
        )
      );
    } catch (error) {
      setNotification(
        failed(
          'Unable to import the files. Please re-authorise the google account or try after some time.'
        )
      );
    }
  };

  return (
    <LayoutTheme>
      <FormProvider {...methods}>
        <UploadGoogleDriveHeader />
        <Grid container rowSpacing={2} columnSpacing={2} p={4}>
          <Grid item xs={6}>
            <WsTeamFilter
              name="team"
              placeHolder="Search For Teams"
              label={'Teams'}
              requireTeam={true}
              multiple={false}
            />
          </Grid>
          <Grid item xs={6}>
            <WsTagsFilter placeHolder="Enter Tags, comma seprated" name="tags" label="Tags" />
          </Grid>
          <Grid item xs={6}>
            <WsAutoComplete
              onValueEnter={onSkillInputKey}
              onTagsChange={onSkillSelection}
              placeholder="Type to Select Skills"
              initialValue={[]}
              optionsInStringArray={skillList}
              multiple={true}
              freeSolo={true}
              name="skills"
              label="Skills"
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={6}>
            <WsLocationSearch
              size="small"
              placeHolder="Select the Country of Candidates Profiles"
              name="locations"
              variant="outlined"
              showMandatorySymbol={true}
              required
            />
          </Grid>
        </Grid>
        <Grid container direction="row" display="flex" pb={2} pr={4}>
          <Grid item xs={9} textAlign="end" p={1}>
            {!accounts.length && (
              <WsTypography variant={WsTypoVariants.TITLE}>
                No accounts added to access google drive. Please add your account here
              </WsTypography>
            )}
          </Grid>
          <Grid item xs={3}>
            <SignupButton
              onClick={() => {
                googleDriveSignIn();
              }}
              fullWidth
              startIcon={getSocialIcon('Google')}
              variant="outlined"
              size="large"
            >
              Sign in with Google
            </SignupButton>
          </Grid>
        </Grid>
        {accounts.length > 0 && (
          <Grid container direction="row" display="flex" mb={2} ml={2}>
            {accounts.map((account, index) => (
              <Grid item xs={2.5} key={index} display="flex" justifyContent="center">
                <Chip
                  label={account.email}
                  sx={{
                    background:
                      account.email === selectedAccount?.email
                        ? WsColors.TheBlues300
                        : WsColors.TheBlues50,
                    '&:hover': {
                      borderColor: WsColors.TheBlues500,
                    },
                  }}
                  size="medium"
                  variant="outlined"
                  onDelete={() => deleteGoogleAccount(account)}
                  onClick={() => {
                    setSelectedAccount(account);
                    loadChildFolders(null, account);
                  }}
                  deleteIcon={<DeleteIcon />}
                />
              </Grid>
            ))}
          </Grid>
        )}
        {selectedAccount && (
          <>
            <Grid container m={1}>
              {selectedFolder.length > 0 && (
                <Grid container mt={5} ml={2}>
                  <Grid item xs={12}>
                    <Button onClick={() => navigateBack()} variant="outlined">
                      Back
                    </Button>
                  </Grid>
                </Grid>
              )}
              <Grid item xs={12} p={2}>
                <WsTypography>
                  {`Please select the folder(s) or click on 'Start Import' to import all files and folder
              under ${getCurrentPath()}`}
                </WsTypography>
              </Grid>
              {folder.map((value, index) => (
                <Grid
                  item
                  key={index}
                  xs={2}
                  display="flex"
                  direction="row"
                  justifyContent="center"
                >
                  <Checkbox size="small" onChange={() => selectImportFolders(value.id ?? '')} />

                  <WsButton onClick={() => loadChildFolders(value, null)} variant="outlined">
                    {value.title ?? ''}
                  </WsButton>
                </Grid>
              ))}
            </Grid>
            {driveDetails?.pageToken && (
              <Grid container mt={2} ml={2} display="flex" direction="row-reverse">
                <Grid item xs={12} p={2}>
                  <WsButton onClick={() => loadMore()} variant="contained">
                    Load More(20)
                  </WsButton>
                </Grid>
              </Grid>
            )}

            <Grid container mt={5} ml={2}>
              <Grid item xs={12}>
                <WsButton onClick={() => startImport()} variant="outlined">
                  Start Import
                </WsButton>
                <WsTypography variant={WsTypoVariants.CAPTION}>
                  Note: All files under the folder and sub-folders of the selected folder will be
                  imported.
                </WsTypography>
              </Grid>
            </Grid>
          </>
        )}
      </FormProvider>
    </LayoutTheme>
  );
};

export default UploadFromGoogleDrive;
